import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import DownloadIcon from '@mui/icons-material/Download';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import DevicesListFilter from 'components/DevicesFilter/DevicesListFilter';
import CsvDownloadWizard from 'components/CsvDownloadWizard';
import GenericModal from 'components/GenericModal';
import Loading from 'components/Loading/Loading';
import MDBox from 'components/MDBox';
import PingPongText from 'components/PingPongText/PingPongText';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import DashboardLayout from 'layouts/LayoutContainers/DashboardLayout';
import { useLayoutEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchBuildingDemandSummary } from 'stores/sensor/homePageSlice';
import { getBuildingDemandSummary } from 'services/dataService';
import { brandColors } from 'types/colors';

import BuildingDemandCard from './BuildingDemandCard';
import BuildingDemandTimeRangeDialogSelect from './BuildingDemandTimeRangeDialogSelect';
import Charts, { demandMeasurements } from './Charts';

const timeRangeOptions = [
  // { label: 'Real Time', value: 'Real Time' },
  { label: 'Hour', value: 'Hour' },
  { label: '4 hours', value: '4 hours' },
  { label: '12 hours', value: '12 hours' },
  { label: 'today', value: 'today' },
  { label: '1 week', value: '1 week' },
  { label: 'Date to date', value: 'DATE_TO_DATE' },
];

const topBarButtonSx = {
  width: '4rem',
  minWidth: '4rem',
  height: '2rem',
  p: 0,
  borderRadius: '7px',
  boxShadow: 'none',
  color: '#fff',
  transition: 'transform 250ms cubic-bezier(0.4, 0, 0.2, 1)',
  transform: 'scale(1)',
  '&:hover': { boxShadow: 'none', transform: 'scale(1.2)' },
};

const topBarIconSx = {
  queryStats: { scale: '2.2' },
  download: { scale: '1.5', ml: 0.5 },
};

const sensorTooltipProps = {
  slotProps: {
    popper: { sx: { zIndex: 50020 } },
  },
};

const csvColumnLabels = Object.fromEntries(demandMeasurements.map(({ field, label }) => [field, label]));

function BuildingDemand() {
  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Jerusalem';
  const [selectedTimeRange, setSelectedTimeRange] = useState(timeRangeOptions[3]);
  const [selectedMeasurements, setSelectedMeasurements] = useState([demandMeasurements[0].field]);
  const [demandData, setDemandData] = useState(null);
  const [isDemandLoading, setIsDemandLoading] = useState(false);
  const [csvWizardOpen, setCsvWizardOpen] = useState(false);
  const demandRequestRef = useRef(0);
  const thermalParameterSelectRef = useRef(null);
  const airParameterSelectRef = useRef(null);
  const [thermalParameterMenuWidth, setThermalParameterMenuWidth] = useState(0);
  const [airParameterMenuWidth, setAirParameterMenuWidth] = useState(0);
  const dispatch = useDispatch();
  const theme = useTheme();
  const isSmallScreenOrLarger = useMediaQuery(theme.breakpoints.up('sm'));
  const { distributor, customer, building, floor, area, hvacEquipment } = useSelector(
    state => state.persistence?.selectedFilterObject || {},
  );
  const buildings = useSelector(state => state.users?.buildings || []);
  const buildingOptions = useSelector(state => state.persistence?.options?.buildings || []);
  const selectedBuilding = buildings.find(
    item => String(item?.id ?? item?.building_id ?? item?.value) === String(building),
  );
  const selectedBuildingOption = buildingOptions.find(item => String(item?.value) === String(building));
  const availableCsvColumns = demandMeasurements.map(({ field }) => field);
  const selectedGraphs = selectedMeasurements;
  const buildingId = building;
  const buildingName =
    selectedBuilding?.name || selectedBuilding?.building_name || selectedBuildingOption?.label || `Building ${building}`;
  const rangeObj = { value: selectedTimeRange.value };
  const sensorStatusButtonSx = [
    topBarButtonSx,
    {
      backgroundColor: '#DEE4F9',
      color: brandColors.BLUE,
      '&:hover': { backgroundColor: '#DEE4F9' },
    },
  ];

  const handleDownloadCSV = () => setCsvWizardOpen(true);

  const loadCsvMeasurements = async (columns, timeframe) => {
    const [startTime, endTime] = typeof timeframe === 'string' && timeframe.includes('_') ? timeframe.split('_') : [];
    const timeRange = startTime && endTime
      ? {
          startDate: `${startTime.slice(8, 10)}.${startTime.slice(5, 7)}.${startTime.slice(0, 4)}`,
          endDate: `${endTime.slice(8, 10)}.${endTime.slice(5, 7)}.${endTime.slice(0, 4)}`,
        }
      : timeframe;
    const result = await getBuildingDemandSummary({
      building,
      floor,
      area,
      hvacEquipment,
      time_range: timeRange,
      userTimeZone,
    });

    return {
      measurements: Object.fromEntries(columns.map(column => [column, result?.chart?.[column] || []])),
    };
  };

  useLayoutEffect(() => {
    const element = thermalParameterSelectRef.current;
    if (!element) {
      return undefined;
    }
    let animationFrameId = null;

    const updateWidth = () => {
      const width = Math.round(element.getBoundingClientRect().width);
      setThermalParameterMenuWidth(currentWidth => (currentWidth === width ? currentWidth : width));
    };

    const scheduleWidthUpdate = () => {
      if (animationFrameId !== null) {
        return;
      }
      animationFrameId = requestAnimationFrame(() => {
        animationFrameId = null;
        updateWidth();
      });
    };

    updateWidth();
    const observer = new ResizeObserver(scheduleWidthUpdate);
    observer.observe(element);
    return () => {
      observer.disconnect();
      if (animationFrameId !== null) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, []);

  useLayoutEffect(() => {
    const element = airParameterSelectRef.current;
    if (!element) {
      return undefined;
    }
    let animationFrameId = null;

    const updateWidth = () => {
      const width = Math.round(element.getBoundingClientRect().width);
      setAirParameterMenuWidth(currentWidth => (currentWidth === width ? currentWidth : width));
    };

    const scheduleWidthUpdate = () => {
      if (animationFrameId !== null) {
        return;
      }
      animationFrameId = requestAnimationFrame(() => {
        animationFrameId = null;
        updateWidth();
      });
    };

    updateWidth();
    const observer = new ResizeObserver(scheduleWidthUpdate);
    observer.observe(element);
    return () => {
      observer.disconnect();
      if (animationFrameId !== null) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, []);

  useLayoutEffect(() => {
    const requestId = demandRequestRef.current + 1;
    demandRequestRef.current = requestId;

    if (!building || building === 'undefined' || building === 'all') {
      setDemandData(null);
      setIsDemandLoading(false);
      return;
    }

    setDemandData(null);
    setIsDemandLoading(true);
    dispatch(
      fetchBuildingDemandSummary({
        distributor,
        customer,
        building,
        floor,
        area,
        hvacEquipment,
        time_range: selectedTimeRange.value,
        userTimeZone,
      }),
    )
      .unwrap()
      .then(result => {
        if (demandRequestRef.current === requestId) {
          setDemandData({
            summary: result?.summary || null,
            aggregates: result?.aggregates || { devices: [], floors: [], areas: [] },
            latest: result?.latest || { devices: [], floors: [], areas: [] },
            chart: result?.chart || {},
            timeRange: result?.timeRange || null,
            fetchedAt: Date.now(),
          });
        }
      })
      .catch(error => {
        if (demandRequestRef.current === requestId) {
          console.error('Building demand fetch failed:', error);
          setDemandData(null);
        }
      })
      .finally(() => {
        if (demandRequestRef.current === requestId) {
          setIsDemandLoading(false);
        }
      });
  }, [dispatch, distributor, customer, building, floor, area, hvacEquipment, selectedTimeRange, userTimeZone]);

  return (
    <DashboardLayout>
      <DashboardNavbar objectName="Building demand" />
      <Typography
        fontSize="11px"
        sx={{
          paddingLeft: { xs: 0, sm: '12px' },
          marginTop: { xs: '-15px', sm: '-5px' },
          textAlign: { xs: 'center', sm: 'left' },
        }}>
        Smart indoor air monitoring & management platform
      </Typography>
      <MDBox pt={1}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <MDBox variant="gradient" borderRadius="lg" coloredShadow="light" sx={{ background: '#DEE4F9', px: 2, py: 0 }}>
              <Box
                className="building-demand-topbar"
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                }}>
                <Typography variant="h4" color="dark">
                  Building demand
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'center', gap: 1 }}>
                  <DevicesListFilter
                    area={true}
                    status={false}
                    search={false}
                    hvacEquipment
                    showRefreshButton={false}
                    flexDirection="row"
                    extraFilterFields={[
                      <FormControl
                        key="thermal-parameter"
                        className="building-demand-parameter-select"
                        ref={thermalParameterSelectRef}
                        sx={{
                          width: '100%',
                          py: 0,
                        }}>
                        <InputLabel id="building-demand-thermal-parameter-label">Thermal</InputLabel>
                        <Select
                          labelId="building-demand-thermal-parameter-label"
                          multiple
                          value={selectedMeasurements.filter(field => field === 'occupancy' || field.startsWith('thermal_'))}
                          label="Thermal"
                          onChange={event => {
                            const { value } = event.target;
                            const thermalMeasurements = typeof value === 'string' ? value.split(',') : value;
                            setSelectedMeasurements(currentMeasurements => [
                              ...currentMeasurements.filter(field => field !== 'occupancy' && !field.startsWith('thermal_')),
                              ...thermalMeasurements,
                            ]);
                          }}
                          renderValue={selectedValues =>
                            demandMeasurements
                              .filter(
                                measurement =>
                                  selectedValues.includes(measurement.field) &&
                                  (measurement.field === 'occupancy' || measurement.field.startsWith('thermal_')),
                              )
                              .map(measurement => measurement.label)
                              .join(', ')
                          }
                          MenuProps={{
                            disableScrollLock: true,
                            PaperProps: {
                              sx: {
                                width: thermalParameterMenuWidth || undefined,
                                maxWidth: thermalParameterMenuWidth || undefined,
                                mt: '-5px',
                              },
                            },
                          }}>
                          {demandMeasurements
                            .filter(measurement => measurement.field === 'occupancy' || measurement.field.startsWith('thermal_'))
                            .map(measurement => (
                              <MenuItem key={measurement.field} value={measurement.field}>
                                <Checkbox checked={selectedMeasurements.includes(measurement.field)} />
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <PingPongText text={measurement.label} speed={4} />
                                </Box>
                              </MenuItem>
                            ))}
                        </Select>
                      </FormControl>,
                      <FormControl
                        key="air-parameter"
                        className="building-demand-parameter-select"
                        ref={airParameterSelectRef}
                        sx={{
                          width: '100%',
                          py: 0,
                        }}>
                        <InputLabel id="building-demand-air-parameter-label">Air</InputLabel>
                        <Select
                          labelId="building-demand-air-parameter-label"
                          multiple
                          value={selectedMeasurements.filter(field => field === 'occupancy' || field.startsWith('air_'))}
                          label="Air"
                          onChange={event => {
                            const { value } = event.target;
                            const airMeasurements = typeof value === 'string' ? value.split(',') : value;
                            setSelectedMeasurements(currentMeasurements => [
                              ...currentMeasurements.filter(field => field !== 'occupancy' && !field.startsWith('air_')),
                              ...airMeasurements,
                            ]);
                          }}
                          renderValue={selectedValues =>
                            demandMeasurements
                              .filter(
                                measurement =>
                                  selectedValues.includes(measurement.field) &&
                                  (measurement.field === 'occupancy' || measurement.field.startsWith('air_')),
                              )
                              .map(measurement => measurement.label)
                              .join(', ')
                          }
                          MenuProps={{
                            disableScrollLock: true,
                            PaperProps: {
                              sx: {
                                width: airParameterMenuWidth || undefined,
                                maxWidth: airParameterMenuWidth || undefined,
                                mt: '-5px',
                              },
                            },
                          }}>
                          {demandMeasurements
                            .filter(measurement => measurement.field === 'occupancy' || measurement.field.startsWith('air_'))
                            .map(measurement => (
                              <MenuItem key={measurement.field} value={measurement.field}>
                                <Checkbox checked={selectedMeasurements.includes(measurement.field)} />
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <PingPongText text={measurement.label} speed={4} />
                                </Box>
                              </MenuItem>
                            ))}
                        </Select>
                      </FormControl>,
                    ]}
                  />
                </Box>

                <Tooltip {...sensorTooltipProps} title="Download charts data as CSV" placement="top" arrow>
                  <Button
                    className="top-bar-btn"
                    sx={sensorStatusButtonSx}
                    color="success"
                    variant="contained"
                    onClick={handleDownloadCSV}>
                    <QueryStatsIcon sx={topBarIconSx.queryStats}></QueryStatsIcon>
                    <DownloadIcon sx={topBarIconSx.download}></DownloadIcon>
                  </Button>
                </Tooltip>

                <Tooltip
                  arrow
                  placement="bottom"
                  slotProps={{ tooltip: { sx: { maxWidth: 360 } } }}
                  title={
                    <>
                      <strong>Chart values</strong>
                      <br />
                      The ~% value is the average of all selected scope values for that five-minute period.
                      <br />
                      (matching/total) shows how many scopes met the threshold out of all scopes included in that average. Demand
                      thresholds are values above 0%; occupancy uses values 10% or more.
                    </>
                  }>
                  <IconButton aria-label="Building demand chart information" sx={{ color: brandColors.BLUE }}>
                    <InfoOutlinedIcon sx={{ fontSize: 28 }} />
                  </IconButton>
                </Tooltip>
                <BuildingDemandTimeRangeDialogSelect
                  buttonTitle="Time Range"
                  color="black"
                  default={selectedTimeRange}
                  dialogTitle="Choose Time Range"
                  onSelectionOK={setSelectedTimeRange}
                  selectLabel="Time Range"
                  selectOptions={timeRangeOptions}
                />
              </Box>
            </MDBox>
          </Grid>
          <Grid item xs={12}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', lg: 'row' },
                gap: 2,
                minHeight: { xs: 0, sm: 460 },
                height: { sm: 'calc(100dvh - 280px)' },
              }}>
              {!building || building === 'undefined' || building === 'all' ? (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', textAlign: 'center' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#282828' }}>
                    Select a building to view Building demand data.
                  </Typography>
                </Box>
              ) : isDemandLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                  <Loading />
                </Box>
              ) : (
                <>
                  <BuildingDemandCard demandData={demandData} selectedTimeRange={selectedTimeRange.value} />
                  {isSmallScreenOrLarger && (
                    <Box sx={{ display: 'flex', flex: 1, minWidth: 0, minHeight: 0, overflow: 'visible' }}>
                      <Charts
                        chart={demandData?.chart || {}}
                        floor={floor}
                        area={area}
                        selectedMeasurements={selectedMeasurements}
                      />
                    </Box>
                  )}
                </>
              )}
            </Box>
          </Grid>
        </Grid>
      </MDBox>
      <GenericModal
        open={csvWizardOpen}
        onClose={() => setCsvWizardOpen(false)}
        title="Download charts data"
        Component={CsvDownloadWizard}
        componentProps={{
          deviceId: buildingId,
          deviceName: buildingName,
          idLabel: 'building id',
          nameLabel: 'building name',
          // Pass the demand chart columns so the CSV selection matches this page.
          availableColumns: availableCsvColumns,
          selectedColumns: selectedGraphs,
          columnLabels: csvColumnLabels,
          currentTimeRange: rangeObj.value,
          fetchMeasurements: loadCsvMeasurements,
          onComplete: () => setCsvWizardOpen(false),
        }}
      />
    </DashboardLayout>
  );
}

export default BuildingDemand;
